import {apiV2, db, FirebaseError} from "api/firebase";
import {uniq} from "lodash";
import RawOrder, {ExpandedOrder} from "ts/api/order";
import {useParams} from "react-router-dom";
import {useEffect, useState} from "react";

type UseShowOrderResult = [
  (RawOrder | ExpandedOrder)?,
  boolean?,
  FirebaseError?
];

type UseShowOrderArgs = {
  orderId?: string;
  isAlive?: boolean;
};
type OrderState = {
  order?: RawOrder | ExpandedOrder;
  isLoading: boolean;
};
export default (args?: UseShowOrderArgs): UseShowOrderResult => {
  const [order, setOrder] = useState<OrderState>({
    isLoading: true,
  });
  const [error, setError] = useState<FirebaseError>();
  const {showId, orderId: paramsOrderId} = useParams<{
    orderId: string;
    showId: string;
  }>();
  const orderId = args?.orderId || paramsOrderId;

  useEffect(() => {
    if (!showId || !orderId) {
      return;
    }

    const ref = db
      .collection("shows")
      .doc(showId)
      .collection("orders")
      .doc(orderId);

    const unsubscribe = ref.onSnapshot(
      async (snap) => {
        if (!snap.exists) {
          setOrder({order: undefined, isLoading: false});
          return;
        }

        setOrder((old) => ({...old, isLoading: true}));

        try {
          const expandedOrder = await expandOrder(
            {...snap.data(), id: snap.id} as RawOrder,
            showId
          );
          setOrder({order: expandedOrder, isLoading: false});
        } catch (e) {
          setOrder({order: undefined, isLoading: false});
        }
      },
      (e) => {
        setError(e);
        setOrder({order: undefined, isLoading: false});
      }
    );

    return () => unsubscribe();
  }, [showId, orderId, args?.isAlive]);

  return [order?.order, order?.isLoading, error];
};

const expandOrder = async (
  order: RawOrder,
  showId: string
): Promise<ExpandedOrder> => {
  const supplierIds = uniq(order.products.map((product) => product.owner.id));
  const supplierCompanies = await apiV2.supplierCompany.getAllByIds({
    supplierCompanyIds: supplierIds.join(","),
  });
  const {exhibitorId, standId} = order;

  const showQuery = db.collection("shows").doc(showId);

  const exhibitorConnection = await apiV2.exhibitor.userConnection.getOne({
    showId,
    exhibitorUserId: exhibitorId,
    oldFirebaseStandId: standId,
  });

  return {
    ...order,
    supplierCompanies,
    ...(exhibitorConnection && {exhibitorConnection}),
  };
};
